import { Loader2, CheckCircle2, AlertTriangle, XCircle, HelpCircle, Wrench, Unlink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { StockStatus } from '@features/inventory';
import { stockVerdict } from '../utils/freeStock';

interface StockStatusBadgeProps {
  status: StockStatus;
  quantityAvailable?: number;
  requestedQuantity?: number;
  binCodes?: string[];
  pendingPickingQty?: number;
  pendingPostingQty?: number;
}

/**
 * The prototype's `crTone`, verbatim: a status badge is a SOFT TINT, never a solid fill.
 *
 *   outline    -> transparent ground, muted ink, a real border
 *   secondary  -> muted ground, muted ink, no border
 *   otherwise  -> `token / 0.1` ground, full-strength token ink, no border
 *
 * Solid `variant="destructive"` fills read far louder than the design intends — a cart of four items
 * became a wall of red (owner, 2026-09-18). Keep new statuses on this scale.
 */
export const BADGE_TONE: Record<string, string> = {
  outline: 'border-border bg-transparent text-muted-foreground',
  secondary: 'border-transparent bg-muted text-muted-foreground',
  success: 'border-transparent bg-success/10 text-success',
  warning: 'border-transparent bg-warning/10 text-warning',
  destructive: 'border-transparent bg-destructive/10 text-destructive',
  info: 'border-transparent bg-info/10 text-info',
};

const statusConfig: Record<
  StockStatus,
  {
    label: string;
    /** A key of BADGE_TONE, not a shadcn variant — see the note above. */
    tone: keyof typeof BADGE_TONE & string;
    icon: React.ComponentType<{ className?: string }>;
    className?: string;
  }
> = {
  loading: {
    label: 'Checking...',
    tone: 'outline',
    icon: Loader2,
    className: 'animate-spin',
  },
  available: {
    label: 'In Stock',
    tone: 'success',
    icon: CheckCircle2,
  },
  low: {
    label: 'Low Stock',
    tone: 'warning',
    icon: AlertTriangle,
  },
  out_of_stock: {
    label: 'Out of Stock',
    tone: 'destructive',
    icon: XCircle,
  },
  error: {
    label: 'Error',
    tone: 'destructive',
    icon: XCircle,
  },
  unknown: {
    // Not 'Unknown': this state means we have not read NetSuite for this line YET, which is a
    // step not yet taken rather than a fact we failed to learn.
    label: 'Not checked',
    tone: 'outline',
    icon: HelpCircle,
  },
  non_catalog: {
    label: 'Manual item',
    tone: 'secondary',
    icon: Wrench,
  },
  not_linked: {
    label: 'Not linked',
    tone: 'warning',
    icon: Unlink,
  },
};

export function StockStatusBadge({
  status,
  quantityAvailable,
  requestedQuantity,
  binCodes,
  pendingPickingQty,
  pendingPostingQty,
}: StockStatusBadgeProps) {
  const config = statusConfig[status];
  const Icon = config.icon;

  const showTooltip = status !== 'loading' && status !== 'unknown';
  const totalCommitted = (pendingPickingQty ?? 0) + (pendingPostingQty ?? 0);
  const hasCommitted = totalCommitted > 0;
  const effectiveAvailable = quantityAvailable !== undefined
    ? Math.max(0, quantityAvailable - totalCommitted)
    : undefined;
  // 14ykpqtv7nd — what the pill's figures speak of: FREE stock once holds are known, as the status is judged. A Low
  // pill reading "5/4" (raw on hand over wanted) contradicted its own status; "2/4" does not.
  const shownAvailable = hasCommitted ? effectiveAvailable : quantityAvailable;

  /** A state that is not about quantity says one plain line; everything else gets the figures card. */
  const plainMessage =
    status === 'error'
      ? 'Failed to fetch stock data'
      : status === 'non_catalog'
        ? 'Non-catalog / manual item (no NetSuite stock tracking) — handle outside the picklist'
        : status === 'not_linked'
          ? 'Not linked to a NetSuite inventory item — resync this MRS to resolve'
          : null;

  const badge = (
    <Badge
      variant="outline"
      // `whitespace-nowrap` is load-bearing with the fixed h-5: two-word labels ("Manual item",
      // "Not checked") wrap inside the pill and spill out of it otherwise.
      className={`h-5 gap-1 whitespace-nowrap px-[7px] text-xs leading-none ${showTooltip ? 'cursor-help' : 'cursor-default'} ${BADGE_TONE[config.tone]}`}
    >
      <Icon className={`h-3 w-3 ${config.className ?? ''}`} />
      {status === 'low' && shownAvailable !== undefined && requestedQuantity !== undefined ? (
        <span>{shownAvailable}/{requestedQuantity}</span>
      ) : (
        <span>{config.label}</span>
      )}
      {hasCommitted && status !== 'loading' && status !== 'unknown' && status !== 'error' && (
        <span className="ml-0.5 text-[10px] text-warning">*</span>
      )}
    </Badge>
  );

  if (!showTooltip || (!plainMessage && quantityAvailable === undefined)) {
    return badge;
  }

  if (plainMessage) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{badge}</TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">{plainMessage}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="start"
          className="w-72 rounded-lg border-border bg-card p-0 text-card-foreground shadow-lg"
        >
          <StockFigures
            label={status === 'low' ? 'Low stock' : config.label}
            onHand={quantityAvailable}
            held={totalCommitted}
            wanted={requestedQuantity}
            binCodes={binCodes}
            verdict={stockVerdict(status, quantityAvailable, totalCommitted, requestedQuantity)}
          />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** How many bin codes the card lists before "+N more" — the rest are on that line's hover. */
const BINS_SHOWN = 4;

/**
 * The stock pill's figures, laid out to be read at a glance (owner, 2026-10-15): what is on the shelf, what other
 * picklists hold of it, what is FREE — the number the status is judged on — and what this line wants, then one
 * sentence saying what that means. The cart's Short popover (`ShortfallPopover`) is the same language for an item.
 */
function StockFigures({
  label,
  onHand,
  held,
  wanted,
  binCodes,
  verdict,
}: {
  label: string;
  onHand: number | undefined;
  /** What other open picklists hold: unpicked plus picked-but-unposted. */
  held: number;
  wanted: number | undefined;
  binCodes: string[] | undefined;
  verdict: string | null;
}) {
  const free = onHand === undefined ? undefined : Math.max(0, onHand - held);
  // Defensive dedupe: a per-serial stock read repeats a bin code once per unit (~1,000 for one item once).
  const bins = binCodes ? Array.from(new Set(binCodes)) : [];
  const shownBins = bins.slice(0, BINS_SHOWN);
  const moreBins = bins.length - shownBins.length;

  return (
    <div className="text-xs">
      <p className="border-b border-border px-3 py-2 font-semibold">{label}</p>

      <dl className="grid grid-cols-3 gap-x-3 gap-y-1 px-3 py-2">
        <dt className="col-span-2 text-muted-foreground">On hand</dt>
        <dd className="text-right font-medium tabular-nums">{onHand ?? '—'}</dd>

        {/* One figure, no picking / posting split (owner, 2026-10-15): the reader needs how much is spoken for. */}
        {held > 0 && (
          <>
            <dt className="col-span-2 text-muted-foreground">Held by other picklists</dt>
            <dd className="text-right font-medium tabular-nums text-warning">−{held}</dd>
          </>
        )}

        {/* One rule across both columns — a border on each cell is split by the column gap. */}
        <div aria-hidden className="col-span-3 border-t border-border" />
        <dt className="col-span-2 font-semibold">Free</dt>
        <dd className="text-right font-semibold tabular-nums">{free ?? '—'}</dd>

        {wanted !== undefined && (
          <>
            <dt className="col-span-2 text-muted-foreground">Wanted</dt>
            <dd className="text-right font-medium tabular-nums">{wanted}</dd>
          </>
        )}
      </dl>

      {bins.length > 0 && (
        <p className="border-t border-border px-3 py-2 text-muted-foreground">
          {bins.length === 1 ? 'Bin' : 'Bins'}:{' '}
          <span className="font-mono text-foreground">{shownBins.join(', ')}</span>
          {moreBins > 0 && (
            // Clipped on purpose, so the whole list stays one hover away (UI-GUIDELINES §8).
            <span className="cursor-help" title={bins.join(', ')}>
              {' '}+{moreBins} more
            </span>
          )}
        </p>
      )}

      {verdict && <p className="border-t border-border px-3 py-2 leading-snug text-foreground">{verdict}</p>}
    </div>
  );
}
